/**
 * Selos de status e prioridade do caso, com as cores do tema (visual v2).
 */
import { AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/utils/cn'
import { CasePriority, CasePriorityLabels, CaseStatus, CaseStatusLabels } from '@/types/case'

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive'

const STATUS_VARIANT: Record<CaseStatus, BadgeVariant> = {
  DRAFT: 'outline',
  ACTIVE: 'default',
  SUSPENDED: 'secondary',
  FINISHED: 'secondary',
  CLOSED_WON: 'secondary',
  CLOSED_LOST: 'destructive',
  CLOSED_SETTLED: 'secondary',
  ARCHIVED: 'outline',
  CONVERTED_TO_LEGAL: 'secondary',
}

export function CaseStatusBadge({ status, className }: { status: CaseStatus; className?: string }) {
  return (
    <Badge variant={STATUS_VARIANT[status] ?? 'secondary'} className={className}>
      {CaseStatusLabels[status] ?? status}
    </Badge>
  )
}

export function CasePriorityText({ priority, className }: { priority: CasePriority; className?: string }) {
  const strong = priority === CasePriority.URGENT || priority === CasePriority.HIGH
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium',
        priority === CasePriority.URGENT ? 'text-destructive' : strong ? 'text-foreground' : 'text-muted-foreground',
        className,
      )}
    >
      {priority === CasePriority.URGENT && <AlertTriangle className="size-3" aria-hidden="true" />}
      {CasePriorityLabels[priority] ?? priority}
    </span>
  )
}

/** Dias até a data prevista: atraso ou até 7 dias restantes; fora disso não mostra nada. */
export function daysUntil(dateIso?: string, today: Date = new Date()): number | null {
  if (!dateIso) return null
  const diff = new Date(dateIso).getTime() - today.getTime()
  return Math.ceil(diff / (1000 * 3600 * 24))
}

export function CaseDeadlineHint({ expectedEndDate }: { expectedEndDate?: string }) {
  const days = daysUntil(expectedEndDate)
  if (days === null) return null
  if (days < 0) return <span className="text-xs text-destructive">{Math.abs(days)} dia(s) em atraso</span>
  if (days <= 7) return <span className="text-xs font-medium text-foreground">{days} dia(s) restantes</span>
  return null
}
